<template>
  <v-flex>
    <v-layout row wrap>
      <v-flex xs12>
        <h2 class="primary--text pb-2">Script Runtime</h2>
        <p>
          Account-wide settings for cb-runtime. The Agentic image runs the tool loop itself
          against an OpenAI-compatible Chat Completions API and optional MCP servers.
          Non-secret values are stored on the account script-engine feature params.
          The LLM API key is stored in Vault (same as email account passwords) and injected at run time.
          Per-script Input JSON can still override them for a single run.
        </p>
      </v-flex>

      <v-flex xs12 sm10 md8 class="mt-3">
        <v-text-field
          v-model="llmEndpoint"
          box
          label="LLM endpoint (OpenAI-compatible)"
          placeholder="https://api.openai.com/v1"
          hint="Base URL. The agent calls {endpoint}/chat/completions (or the full path if you include it)."
          persistent-hint
          @input="emitAttrs"
        ></v-text-field>
      </v-flex>

      <v-flex xs12 sm10 md8 class="mt-3">
        <v-text-field
          v-model="llmApiKey"
          box
          :type="showKey ? 'text' : 'password'"
          label="LLM API key"
          :placeholder="keyConfigured ? '•••••••• (stored in Vault — enter a new key to replace)' : 'sk-…'"
          :hint="keyHint"
          persistent-hint
          append-icon="visibility"
          @click:append="showKey = !showKey"
          @input="emitKey"
        ></v-text-field>
      </v-flex>

      <v-flex xs12 sm6 md4 class="mt-3">
        <v-text-field
          v-model="llmModel"
          box
          label="LLM model"
          placeholder="gpt-4o-mini"
          hint="Model id passed to the chat completions API."
          persistent-hint
          @input="emitAttrs"
        ></v-text-field>
      </v-flex>

      <v-flex xs12 sm6 md4 class="mt-3">
        <v-text-field
          v-model="maxSteps"
          box
          type="number"
          min="1"
          max="50"
          label="Agent max steps"
          hint="Tool-loop iterations. Default 8 when empty."
          persistent-hint
          @input="emitAttrs"
        ></v-text-field>
      </v-flex>

      <v-flex xs12 class="mt-4">
        <p class="caption grey--text">
          Advanced: non-secret values under Features → Lifecycle callbacks as
          <code>params.llm_endpoint</code>, <code>params.llm_model</code>,
          <code>params.max_steps</code>.
          MCP servers: <code>params.mcpServers</code> or per-run Input JSON.
          Do not put <code>params.llm_api_key</code> in Features — use this form (Vault).
        </p>
      </v-flex>
    </v-layout>
  </v-flex>
</template>

<script>
import { logFeature } from "@/helpers/featureActivity"

function parseLifecycleAttrs(raw) {
  if (!raw) return { params: {} }
  if (typeof raw === "object") {
    var obj = raw
    if (!obj.params || typeof obj.params !== "object") obj.params = {}
    return obj
  }
  try {
    var parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== "object") return { params: {} }
    if (!parsed.params || typeof parsed.params !== "object") parsed.params = {}
    return parsed
  } catch (e) {
    return { params: {} }
  }
}

function setOrDelete(params, key, value) {
  if (value) {
    params[key] = value
  } else {
    delete params[key]
  }
}

export default {
  name: "account-config-runtime",
  props: ["account"],
  data: function () {
    return {
      llmEndpoint: "",
      llmApiKey: "",
      llmModel: "",
      maxSteps: "",
      showKey: false,
      syncing: false,
    }
  },
  computed: {
    keyConfigured: function () {
      return !!(this.account && this.account.llmApiKeyConfigured)
    },
    keyHint: function () {
      if (this.keyConfigured) {
        return "Key is stored in Vault. Leave blank to keep the existing key; enter a new value to replace it."
      }
      return "Stored in Vault and sent as Authorization: Bearer at run time. Optional for local models that do not require a key."
    },
  },
  watch: {
    account: {
      immediate: true,
      deep: true,
      handler: function () {
        this.loadFromAccount()
      },
    },
  },
  methods: {
    loadFromAccount: function () {
      if (!this.account) return
      this.syncing = true
      var attrs = parseLifecycleAttrs(this.account.lifeCycleCallbacks)
      var params = attrs.params || {}
      this.llmEndpoint = params.llm_endpoint != null ? String(params.llm_endpoint) : ""
      // Never load the key from attrs — it lives in Vault only.
      this.llmApiKey = ""
      this.llmModel = params.llm_model != null ? String(params.llm_model) : ""
      this.maxSteps =
        params.max_steps != null && params.max_steps !== ""
          ? String(params.max_steps)
          : ""
      var self = this
      this.$nextTick(function () {
        self.syncing = false
      })
    },
    emitKey: function () {
      if (this.syncing || !this.account) return
      var nextKey = (this.llmApiKey || "").trim()
      logFeature("runtime-vault", "key-updated", "Runtime LLM API key marked for Vault save", {
        configured: !!nextKey,
      })
      this.$emit("update-account-data", {
        key: "changedLlmApiKey",
        value: nextKey,
      })
    },
    emitAttrs: function () {
      if (this.syncing || !this.account) return
      var attrs = parseLifecycleAttrs(this.account.lifeCycleCallbacks)
      if (!attrs.params) attrs.params = {}

      setOrDelete(attrs.params, "llm_endpoint", (this.llmEndpoint || "").trim())
      // Never persist API key in lifecycle attrs.
      delete attrs.params.llm_api_key
      delete attrs.params.llmApiKey
      delete attrs.params.openai_api_key
      setOrDelete(attrs.params, "llm_model", (this.llmModel || "").trim())

      var stepsRaw = (this.maxSteps || "").trim()
      if (stepsRaw) {
        var steps = parseInt(stepsRaw, 10)
        if (!isNaN(steps) && steps > 0) {
          attrs.params.max_steps = steps
        }
      } else {
        delete attrs.params.max_steps
      }

      logFeature("runtime-vault", "attrs", "Runtime non-secret settings updated", {
        hasEndpoint: !!(attrs.params && attrs.params.llm_endpoint),
        hasModel: !!(attrs.params && attrs.params.llm_model),
      })
      this.$emit("update-account-data", {
        key: "lifeCycleCallbacks",
        value: JSON.stringify(attrs, null, 2),
      })
    },
  },
}
</script>
